import {
    CircleCheckIcon,
    CircleCrossIcon,
    CircleInfoIcon,
    CirclePauseIcon,
    CircleSubtractIcon,
    LinkIcon,
    PinIcon,
} from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { Seller, XolaPartnerStatus } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { getDefaultLogoUrl } from "@/models/Seller";

interface PartnerDistributionProfileProps {
    seller: Seller;
    className?: string;
    status?: string;
}

export const PartnerDistributionProfile = ({ seller, className, status }: PartnerDistributionProfileProps) => {
    const website = seller.website ?? "";
    const address = [seller.streetAddress, seller.city, seller.state].join(", ");
    const { aboutBusiness } = seller?.preferences?.network ?? {};

    return (
        <div className={clsx("no-translate flex space-x-4", className)}>
            <div className="mt-1 flex-shrink-0">
                <img
                    alt={`Logo for ${seller.name}`}
                    src={getDefaultLogoUrl(seller, "small")}
                    className="no-translate h-15 w-15 rounded"
                />
            </div>
            <div className="space-y-2">
                <div>
                    <div className="flex justify-between">
                        <NoTranslate as="p" className={clsx(status && "w-2/3", "break-words text-md font-bold")}>
                            {seller.name}
                        </NoTranslate>
                        {status ? <NetworkPartnerStatusBadge status={status} /> : null}
                    </div>

                    {website ? (
                        <p className="space-x-1">
                            <LinkIcon />
                            <NoTranslate>{website}</NoTranslate>
                        </p>
                    ) : null}
                    {address ? (
                        <p className="space-x-1">
                            <PinIcon />
                            <NoTranslate>{address}</NoTranslate>
                        </p>
                    ) : null}
                </div>
                <div>
                    <p className="mt-1 text-sm font-bold">About the business</p>
                    <NoTranslate as="p">{aboutBusiness}</NoTranslate>
                </div>
            </div>
        </div>
    );
};

// Utility function for rendering status badge
export const NetworkPartnerStatusBadge = ({
    status,
    isInitiator = false,
}: {
    status?: string;
    isInitiator?: boolean;
}) => {
    if (!status) return null;

    // Determine color and component based on status
    let color = "";
    let component = null;
    let formattedStatus = "";
    if (isInitiator && status.toLowerCase() === XolaPartnerStatus.PENDING) {
        color = "warning";
        component = <CircleInfoIcon />;
        formattedStatus = "Requested";
    } else {
        switch (status.toLowerCase()) {
            case XolaPartnerStatus.ACCEPTED:
                color = "success";
                component = <CircleCheckIcon />;
                break;
            case XolaPartnerStatus.PENDING:
                color = "warning";
                component = <CirclePauseIcon />;
                break;
            case XolaPartnerStatus.DECLINED:
                color = "danger";
                component = <CircleCrossIcon />;
                break;
            case XolaPartnerStatus.DISCONNECTED:
                color = "danger";
                component = <CircleSubtractIcon />;
                break;
            default:
                // If the status is not recognized, return null (no badge)
                return null;
        }

        formattedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }

    return (
        <Badge color={color} size="small" data-testid="status-badge">
            <span>
                {component} {formattedStatus}
            </span>
        </Badge>
    );
};
